import React, { useEffect } from 'react';
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import Footer from './Footer';
import { TbCurrencyRupee, TbHomeSpark, TbTools, TbFileCertificate } from 'react-icons/tb';

function ResidentialSolar() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const renderIcon = (IconComponent, palette) => {
    const { background, color, border, shadow } = palette;
    return (
      <span
        className="icon-badge"
        style={{ backgroundColor: background, color, border: `1px solid ${border}`, boxShadow: `0 14px 26px ${shadow}` }}
        aria-hidden="true"
      >
        <IconComponent size={30} focusable="false" />
      </span>
    );
  };

  return (
    <>
      <Helmet>
        <title>Residential Solar Panel Installation | Lunar Electric | Home Solar Solutions India</title>
        <meta name="description" content="Get residential solar panel installation for your home with Lunar Electric. Transparent pricing, government subsidies, 1-year free maintenance. Expert home solar solutions across India." />
        <meta name="keywords" content="residential solar panels, home solar installation, rooftop solar for homes, residential solar India, home solar power, solar panels for houses, residential solar cost, home solar solutions, Lunar Electric residential solar" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:title" content="Residential Solar Panel Installation | Lunar Electric" />
        <meta property="og:description" content="Get residential solar panel installation for your home with Lunar Electric. Transparent pricing, government subsidies, and 1-year free maintenance." />
        <meta property="og:url" content="https://lunarelectric.in/residential-solar" />
        <meta name="twitter:title" content="Residential Solar Panel Installation | Lunar Electric" />
        <meta name="twitter:description" content="Get residential solar panel installation for your home with Lunar Electric. Transparent pricing, government subsidies, and 1-year free maintenance." />
        <link rel="canonical" href="https://lunarelectric.in/residential-solar" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Residential Solar Panel Installation",
          "provider": {
            "@type": "Organization",
            "name": "Lunar Electric",
            "url": "https://lunarelectric.in"
          },
          "areaServed": {
            "@type": "Country",
            "name": "India"
          },
          "description": "Professional residential solar panel installation services for homes across India with transparent pricing, government subsidies, and 1-year free maintenance",
          "offers": {
            "@type": "Offer",
            "priceRange": "₹1,40,000 - ₹4,00,000",
            "priceCurrency": "INR"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://lunarelectric.in/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Residential Solar",
              "item": "https://lunarelectric.in/residential-solar"
            }
          ]
        })}</script>
      </Helmet>
      <a href="#main-content" className="skip-to-main" style={{position: 'absolute', left: '-9999px', zIndex: 9999}} onFocus={(e) => e.target.style.left = '0'}>Skip to main content</a>
      <div className="residential-solar-page" id="main-content">
      <Navbar />
      
      {/* Hero Section */}
      <section className="residential-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Residential Solar Panel Installation</h1>
            <p className="hero-subtitle">Power Your Home with Clean, Affordable Solar Energy</p>
            <p className="hero-description">
              Transform your home into an energy-efficient powerhouse with our expert residential solar panel installation services. 
              Get transparent pricing, government subsidies, and 1-year free maintenance from Lunar Electric.
            </p>
            <div className="hero-buttons">
              <Link to="/solar-calculator" className="btn-primary">
                Calculate Your Savings
              </Link>
              <Link to="/callback" className="btn-secondary">
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="residential-benefits">
        <div className="container">
          <h2>Why Choose Residential Solar with Lunar Electric?</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                {renderIcon(TbCurrencyRupee, {
                  background: '#E8F5E8',
                  color: '#1B5E20',
                  border: 'rgba(76, 175, 80, 0.35)',
                  shadow: 'rgba(46, 125, 50, 0.18)',
                })}
              </div>
              <h3>Save Up to 90% on Electricity Bills</h3>
              <p>Generate your own clean energy and reduce your monthly electricity costs significantly with our efficient solar panel systems.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                {renderIcon(TbHomeSpark, {
                  background: '#E3F2FD',
                  color: '#0D47A1',
                  border: 'rgba(33, 150, 243, 0.3)',
                  shadow: 'rgba(25, 118, 210, 0.18)',
                })}
              </div>
              <h3>Perfect for Indian Homes</h3>
              <p>Our solar solutions are specifically designed for Indian weather conditions and residential requirements, ensuring maximum efficiency.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                {renderIcon(TbTools, {
                  background: '#FFF3E0',
                  color: '#EF6C00',
                  border: 'rgba(255, 152, 0, 0.32)',
                  shadow: 'rgba(255, 152, 0, 0.2)',
                })}
              </div>
              <h3>1 Year Free Maintenance</h3>
              <p>Comprehensive cleaning and service included for the first year, ensuring your solar panels perform at peak efficiency.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                {renderIcon(TbFileCertificate, {
                  background: '#F3E5F5',
                  color: '#6A1B9A',
                  border: 'rgba(156, 39, 176, 0.3)',
                  shadow: 'rgba(123, 31, 162, 0.18)',
                })}
              </div>
              <h3>Government Subsidies Applied</h3>
              <p>We handle all subsidy applications and ensure you get the maximum benefits from government solar schemes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="residential-process">
        <div className="container">
          <h2>Our Simple 4-Step Process</h2>
          <div className="process-steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Free Consultation</h3>
              <p>Schedule a free home visit where our experts assess your roof and energy needs.</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Custom Design</h3>
              <p>We create a personalized solar system design optimized for your home's energy consumption.</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Professional Installation</h3>
              <p>Our certified technicians install your solar panels with minimal disruption to your daily routine.</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>Activation & Monitoring</h3>
              <p>We activate your system and provide monitoring tools to track your energy savings.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="residential-cta">
        <div className="container">
          <h2>Ready to Go Solar?</h2>
          <p>Join thousands of Indian families who have already made the switch to clean, affordable solar energy.</p>
          <div className="cta-buttons">
            <Link to="/callback" className="btn-primary">
              Get Free Quote
            </Link>
            <Link to="/solar-calculator" className="btn-secondary">
              Calculate Savings
            </Link>
          </div>
        </div>
      </section>

      </div>
      <Footer />
    </>
  );
}

export default ResidentialSolar;
